import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Trash2, Building2, RefreshCw, Users, Check, ChevronsUpDown } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface DemoStaffMember {
  id: string;
  employee_id: string;
  first_name: string;
  last_name: string;
  email: string;
  department?: string;
  position_title?: string;
  job_type?: string;
  school_name?: string;
  principal_name?: string;
  phone?: string;
  grade?: string;
  employment_status: string;
}

export default function DemoStaffManager() {
  const [demoStaff, setDemoStaff] = useState<DemoStaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStaff, setSelectedStaff] = useState<DemoStaffMember | null>(null);
  const [newSchool, setNewSchool] = useState<string>('');
  const [schoolOptions, setSchoolOptions] = useState<string[]>([]);
  const [schoolSearchOpen, setSchoolSearchOpen] = useState(false);

  const loadDemoStaff = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('demo_staff')
        .select('*')
        .order('last_name');

      if (error) throw error;
      
      setDemoStaff(data || []);
      
      // Extract unique schools
      const schools = [...new Set((data || []).map(s => s.school_name).filter(Boolean))];
      setSchoolOptions(schools as string[]);
      
    } catch (error) {
      console.error('Error loading demo staff:', error);
      toast.error('Failed to load demo staff');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDemoStaff();
  }, []);

  const handleDeleteStaff = async (staff: DemoStaffMember) => {
    try {
      const { error } = await supabase
        .from('demo_staff')
        .delete()
        .eq('id', staff.id);

      if (error) throw error;

      toast.success(`Demo staff "${staff.first_name} ${staff.last_name}" deleted successfully.`);
      await loadDemoStaff();
    } catch (error) {
      console.error('Error deleting demo staff:', error);
      toast.error('Failed to delete demo staff member.');
    }
  };

  const handleSchoolTransfer = async () => {
    if (!selectedStaff || !newSchool) return;

    try {
      const { error } = await supabase
        .from('demo_staff')
        .update({ school_name: newSchool })
        .eq('id', selectedStaff.id);

      if (error) throw error;

      toast.success(`${selectedStaff.first_name} ${selectedStaff.last_name} transferred to ${newSchool}`);
      setSelectedStaff(null);
      setNewSchool('');
      await loadDemoStaff();
    } catch (error) {
      console.error('Error transferring demo staff:', error);
      toast.error('Failed to transfer demo staff to new school.');
    }
  };

  const getStatusBadge = (status: string) => {
    return status === 'active' ? (
      <Badge variant="default">Active</Badge>
    ) : (
      <Badge variant="secondary">Inactive</Badge>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Demo Staff Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-slate-200 rounded w-1/4"></div>
            <div className="h-32 bg-slate-200 rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Users className="w-5 h-5" />
          Demo Staff Management ({demoStaff.length} records)
        </CardTitle>
        <Button variant="outline" onClick={loadDemoStaff} disabled={loading}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>School</TableHead>
                <TableHead>Position</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {demoStaff.map((staff) => (
                <TableRow key={staff.id}>
                  <TableCell className="font-medium">
                    {staff.first_name} {staff.last_name}
                    <div className="text-xs text-muted-foreground">
                      ID: {staff.employee_id}
                    </div>
                  </TableCell>
                  <TableCell>{staff.email}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      {staff.school_name || 'N/A'}
                    </div>
                  </TableCell>
                  <TableCell>{staff.position_title || 'N/A'}</TableCell>
                  <TableCell>{staff.department || 'N/A'}</TableCell>
                  <TableCell>{getStatusBadge(staff.employment_status)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedStaff(staff)}
                          >
                            <Building2 className="h-4 w-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-md">
                          <DialogHeader>
                            <DialogTitle>Transfer to Different School</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div>
                              <p className="text-sm text-muted-foreground">
                                Transfer {staff.first_name} {staff.last_name} to a different school
                              </p>
                              <p className="text-sm font-medium mt-1">
                                Current: {staff.school_name || 'N/A'}
                              </p>
                            </div>
                            
                            <div className="space-y-2">
                              <label className="text-sm font-medium">Select New School</label>
                              <Popover open={schoolSearchOpen} onOpenChange={setSchoolSearchOpen}>
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="outline"
                                    role="combobox"
                                    aria-expanded={schoolSearchOpen}
                                    className="w-full justify-between"
                                  >
                                    {newSchool ? newSchool : "Search and select school..."}
                                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-full p-0" align="start">
                                  <Command>
                                    <CommandInput placeholder="Search schools..." className="h-9" />
                                    <CommandList className="max-h-[200px]">
                                      <CommandEmpty>No school found.</CommandEmpty>
                                      <CommandGroup>
                                        {schoolOptions
                                          .filter(school => school !== staff.school_name)
                                          .map((school) => (
                                            <CommandItem
                                              key={school}
                                              value={school}
                                              onSelect={(currentValue) => {
                                                setNewSchool(currentValue === newSchool ? "" : currentValue);
                                                setSchoolSearchOpen(false);
                                              }}
                                            >
                                              <Building2 className="mr-2 h-4 w-4" />
                                              {school}
                                              <Check
                                                className={`ml-auto h-4 w-4 ${
                                                  newSchool === school ? "opacity-100" : "opacity-0"
                                                }`}
                                              />
                                            </CommandItem>
                                          ))
                                        }
                                      </CommandGroup>
                                    </CommandList>
                                  </Command>
                                </PopoverContent>
                              </Popover>
                            </div>
                            
                            <div className="flex justify-end gap-2">
                              <Button variant="outline" onClick={() => {
                                setSelectedStaff(null);
                                setNewSchool('');
                                setSchoolSearchOpen(false);
                              }}>
                                Cancel
                              </Button>
                              <Button onClick={handleSchoolTransfer} disabled={!newSchool}>
                                Transfer
                              </Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>

                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Demo Staff</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete {staff.first_name} {staff.last_name}? 
                              This is demo data and can be safely removed.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeleteStaff(staff)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        
        {demoStaff.length === 0 && (
          <div className="text-center py-8">
            <p className="text-muted-foreground">No demo staff records found</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}